import {
    Alert,
    AlertColor,
    AlertProps,
    Box,
    Snackbar,
    SnackbarProps,
} from "@mui/material";
import React, {
    forwardRef,
    ReactNode,
    useImperativeHandle,
    useState,
} from "react";
import CampaignIcon from "@mui/icons-material/Campaign";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import HelpIcon from "@mui/icons-material/Help";
import { AiFillInfoCircle } from "react-icons/ai";

import * as Styles from "styles/messages/attentionMessage.styles";
type Message = {
    message: string | React.ElementType;
    severity: AlertColor;
    autoHideDuration?: number | null;
};
export type MessageRef = {
    showMessage: (
        message: string | React.ElementType,
        severity: AlertColor,
        autoHideDuration?: number | null,
    ) => void;
} | null;

export const Message = forwardRef<MessageRef | undefined, any>(
    (props: any, ref) => {
        const [isMessageWindowOpen, setIsMessageWindowOpen] =
            React.useState(false);
        const [queue, setQueue] = useState<Message[]>([]);

        //* Functions //
        useImperativeHandle(ref, () => ({
            showMessage: async (
                message: string | React.ElementType,
                severity: AlertColor,
                autoHideDuration?: number | null,
            ) => {
                setQueue((prevQueue) => [
                    ...prevQueue,
                    { message: message, severity: severity, autoHideDuration },
                ]);
                setIsMessageWindowOpen(true);
                await new Promise((resolve) => {
                    setTimeout(() => {
                        setIsMessageWindowOpen(false);
                        setTimeout(() => {
                            setQueue([]);
                        }, 200);
                    }, 10000);
                    return resolve;
                });
            },
        }));

        const handleSnackbarClose = async () => {
            setIsMessageWindowOpen(false);

            await new Promise((resolve) =>
                setTimeout(() => {
                    setQueue((prevQueue) => {
                        prevQueue.splice(0, 1);
                        if (prevQueue.length > 0) {
                            setIsMessageWindowOpen(true);
                            return prevQueue;
                        } else {
                            return [];
                        }
                    });
                    return resolve;
                }, 200),
            );
        };

        //* Props //
        const SnackbarOptions: SnackbarProps = {
            onClose: async () => await handleSnackbarClose(),
            open: isMessageWindowOpen,
            autoHideDuration: queue[0]?.autoHideDuration,
            //* Position & size
            sx: queue[0]?.severity
                ? Styles.AlertCSS(queue[0]?.severity).__postion
                : {},
        };

        function getIcon(
            severity: AlertColor | undefined,
        ): ReactNode | undefined {
            switch (severity) {
                case "info":
                    return (
                        <Box
                            component={AiFillInfoCircle}
                            sx={Styles.IconCSS(severity)}
                        />
                    );
                case "warning":
                    return <CampaignIcon sx={Styles.IconCSS(severity)} />;
                case "error":
                    return <ErrorIcon sx={Styles.IconCSS(severity)} />;
                case "success":
                    return <CheckCircleIcon sx={Styles.IconCSS(severity)} />;
                default:
                    return undefined;
            }
        }

        const AlertOptions: AlertProps = {
            onClose: async () => await handleSnackbarClose(),
            elevation: 6,
            variant: "filled",
            severity: queue[0]?.severity ? queue[0]?.severity : undefined,
            icon: queue[0]?.severity ? getIcon(queue[0]?.severity) : undefined,
            //* Styling
            sx: queue[0]?.severity
                ? Styles.AlertCSS(queue[0]?.severity).__style
                : {},
        };

        return (
            <Box>
                <Snackbar {...SnackbarOptions}>
                    <Alert {...AlertOptions}>
                        {typeof queue[0]?.message === "string" ? (
                            queue[0]?.message
                        ) : (
                            <Box component={queue[0]?.message} />
                        )}
                    </Alert>
                </Snackbar>
            </Box>
        );
    },
);
